import React, { useState, useRef, useEffect, Suspense, lazy, useCallback } from 'react';
import { Drawer, Typography, Divider, Tree, Space, Card, Row, Col, Image, Skeleton, Button } from 'antd';
import { useTheme } from '../../contexts/ThemeContext';

import { 
  BookOutlined, 
  AppstoreOutlined, 
  UserOutlined, 
  MoneyCollectOutlined, 
  SwapOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  InboxOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  WalletOutlined,
  EditOutlined,
  StopOutlined,
  MenuOutlined,
  LeftOutlined
} from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import styled from 'styled-components';
import { preloadVideoMetadata, sectionVideoMap, findParentSection } from './videoUtils';

const { Title, Paragraph, Text } = Typography;

// Componente Image optimizado con lazy loading
const OptimizedImage: React.FC<{
  src: string;
  alt: string;
  style?: React.CSSProperties;
}> = ({ src, alt, style }) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (observerRef.current && imgRef.current) {
      observerRef.current.unobserve(imgRef.current);
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setLoaded(true);
          if (observerRef.current && imgRef.current) {
            observerRef.current.unobserve(imgRef.current);
            observerRef.current.disconnect();
          }
        }
      },
      { 
        rootMargin: '200px 0px', // Precargar 200px antes de que sea visible
        threshold: 0.1 
      }
    );

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      if (observerRef.current && imgRef.current) {
        observerRef.current.unobserve(imgRef.current);
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div ref={imgRef} style={{ ...style, minHeight: loaded ? 'auto' : '200px' }}>
      {loaded ? (
        <Image
          src={src}
          alt={alt}
          preview={false}
          style={style}
        />
      ) : (
        <Skeleton.Image style={{ width: '100%', height: '200px' }} />
      )}
    </div>
  );
};

// LazyVideo component for YouTube embeds
// Improved LazyVideo component to prevent the base.js call error
const LazyVideo: React.FC<{ src: string; title: string; width: string | number }> = ({ src, title, width }) => {
  const [loaded, setLoaded] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [youtubeId, setYoutubeId] = useState<string>('');
  
  // More defensive YouTube ID extraction
  const getYouTubeId = useCallback((url: string): string => {
    if (!url) return '';
    
    try {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? match[2] : '';
    } catch {
      return '';
    }
  }, []);

  // Set YouTube ID only once and only if src exists
  useEffect(() => {
    if (src) {
      setYoutubeId(getYouTubeId(src));
    }
  }, [src, getYouTubeId]);

  // Safer IntersectionObserver implementation
  useEffect(() => {
    // Always clean up previous observer first
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Only create observer if container exists and not already loaded
    if (containerRef.current && !loaded) {
      try {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            if (entries[0]?.isIntersecting) {
              setLoaded(true);
            }
          },
          { rootMargin: '200px 0px', threshold: 0.1 }
        );

        observerRef.current.observe(containerRef.current);
      } catch (e) {
        // Fallback: just load the video if observer fails
        setLoaded(true);
      }
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loaded]);

  // Style definitions unchanged
  const youtubeContainerStyle = {
    position: 'relative' as const,
    paddingBottom: '56.25%',
    height: 0,
    overflow: 'hidden' as const,
    width: width,
    maxWidth: '100%',
    margin: '0 auto',
  };

  const youtubeIframeStyle = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
  };

  return (
    <div ref={containerRef} style={{ width: width, maxWidth: '100%', margin: '0 auto' }}>
      {loaded ? (
        <div style={youtubeContainerStyle}>
          {youtubeId ? (
            // Use a more reliable embed approach without jsapi
            <iframe 
              src={`https://www.youtube.com/embed/${youtubeId}?rel=0`}
              title={title || 'YouTube video'}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={youtubeIframeStyle}
            />
          ) : (
            <div style={{ ...youtubeIframeStyle, backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span>Video unavailable</span>
            </div>
          )}
        </div>
      ) : (
        <div style={youtubeContainerStyle}>
          <Skeleton.Image style={{ width: '100%', height: '100%', position: 'absolute' }} />
        </div>
      )}
    </div>
  );
};

// Estilos personalizados para el Tree
const StyledTree = styled(Tree)<{ $theme: 'dark' | 'light' }>`
  .ant-tree-node-content-wrapper {
    padding: 0;
    margin: 0;
    transition: all 0.3s;
  }
  
  .ant-tree-treenode {
    padding: 2px;
    margin: 0;
  }
  
  .ant-tree-switcher {
    background: transparent;
  }
  
  .ant-tree-node-selected {
    background-color: ${props => props.$theme === 'dark' ? 'rgba(255, 77, 79, 0.25)' : 'rgba(255, 77, 79, 0.15)'} !important;
    font-weight: bold !important;
   padding: 1px 5px;
  }
  
  .ant-tree-node-content-wrapper:hover {
    background-color: ${props => props.$theme === 'dark' ? 'rgba(255, 77, 79, 0.15)' : 'rgba(255, 77, 79, 0.08)'};
  }
  
  .ant-tree-list-holder-inner {
    background-color: ${props => props.$theme === 'dark' ? 'transparent' : '#f7f7f7'};
    border-radius: 6px;
  }
`;

// Mobile-responsive container layout
const DocContainer = styled.div`
  display: flex;
  height: calc(100vh - 120px);
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

// Mobile-responsive sidebar
const DocSidebarContainer = styled.div<{ $theme: 'dark' | 'light'; $isMobile: boolean; $visible: boolean }>`
  width: 280px;
  border-right: 1px solid ${props => props.$theme === 'dark' ? '#333' : '#f0f0f0'};
  padding: 16px;
  margin-right: 16px;
  overflow-y: auto;
  background-color: ${props => props.$theme === 'dark' ? 'transparent' : '#f7f7f7'};
  border-radius: 8px;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    position: absolute;
    z-index: 10;
    width: 100%;
    height: ${props => props.$visible ? 'auto' : '0'};
    max-height: ${props => props.$visible ? '70vh' : '0'};
    opacity: ${props => props.$visible ? 1 : 0};
    overflow: hidden;
    margin-right: 0;
    border-right: none;
    visibility: ${props => props.$visible ? 'visible' : 'hidden'};
    border-bottom: ${props => props.$visible ? `1px solid ${props.$theme === 'dark' ? '#333' : '#f0f0f0'}` : 'none'};
    box-shadow: ${props => props.$visible ? '0 4px 12px rgba(0,0,0,0.1)' : 'none'};
    transform: translateY(${props => props.$visible ? '0' : '-10px'});
    padding: ${props => props.$visible ? '16px 12px' : '0'}; // Adjusted padding for better mobile appearance
  }
`;
const MobileBackdrop = styled.div<{ $visible: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$visible ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 5;
    transition: opacity 0.3s ease;
    opacity: ${props => props.$visible ? 1 : 0};
  }
`;

// Mobile navigation toggle button
const MobileNavToggle = styled(Button)`
  display: none;
  position: fixed;
  bottom: 20px; // Moved higher up from 80px to avoid confusion with bottom elements
  right: 20px;
  z-index: 999; // Increased z-index to ensure it's always on top
  box-shadow: 0 4px 12px rgba(0,0,0,0.25); // Stronger shadow for better visibility
  width: 48px; // Fixed width
  height: 48px; // Fixed height
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// Content area with responsive padding
const ContentArea = styled.div<{ $isMobile: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.$isMobile ? '0 0' : '0 8px'}; // Remove side padding in mobile

  scroll-behavior: smooth;
  ${props => props.$isMobile ? 'padding-top: 8px;' : ''}
`;

// Responsive card styling
const ResponsiveCard = styled(Card)<{ $isMobile?: boolean }>`
  .ant-card-body {
    padding: ${props => props.$isMobile ? '12px' : '24px'};
  }
`;

// Estilo para card seleccionada
// Nuevo estilo para secciones destacadas
const HighlightedSection = styled.div<{ $isSelected: boolean; $theme: 'dark' | 'light' }>`
  padding: 8px;
  margin: -8px;
  border-radius: 6px;
  transition: all 0.3s;
  background-color: ${props => props.$isSelected 
    ? props.$theme === 'dark' ? 'rgba(24, 144, 255, 0.2)' : 'rgba(24, 144, 255, 0.1)' 
    : 'transparent'};
  border-left: 4px solid ${props => props.$isSelected 
    ? props.$theme === 'dark' ? '#1890ff' : '#1890ff' 
    : 'transparent'};
`;

// Componente Skeleton para la carga
const SectionSkeleton = () => (
  <ResponsiveCard>
    <Skeleton active avatar paragraph={{ rows: 4 }} />
    <Divider />
    <Row gutter={[16, 16]}>
      <Col span={12}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </Col>
      <Col span={12}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </Col>
    </Row>
    <Divider />
    <Skeleton.Image style={{ width: '100%', height: 200 }} />
  </ResponsiveCard>
);

interface DocumentationProps {
  open: boolean;
  onClose: () => void;
}

// Mobile responsive text style
const responsiveTextStyle = (isMobile: boolean) => ({
  fontSize: isMobile ? '1em' : '1.1em',
  lineHeight: '1.5',
});

// Creamos un contexto para compartir el subsection seleccionado
interface DocumentationContextType {
  selectedSubsection: string;
  setSelectedSubsection: (subsection: string) => void;
  preloadSectionVideos: (sectionId: string) => void;
}

const DocumentationContext = React.createContext<DocumentationContextType>({
  selectedSubsection: '',
  setSelectedSubsection: () => {},
  preloadSectionVideos: () => {}
});

// Componentes de las secciones con isMobile prop
const Introduction = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
  <ResponsiveCard $isMobile={isMobile}>
    <Title level={isMobile ? 5 : 4}>Bienvenido al sistema</Title>
    <Paragraph style={responsiveTextStyle(isMobile)}>
      ¡Bienvenido a tu capacitación! Para comenzar, es fundamental comprender el rol esencial que desempeñarás en este sistema.
      Tu función tiene dos aspectos principales:
    </Paragraph>
    
    <ul style={{ fontSize: isMobile ? '1em' : '1.1em', lineHeight: '1.6', marginBottom: isMobile ? '10px' : '20px' }}>
      <li><Text strong>Gestión de solicitudes:</Text> Te encargarás de validar y procesar aquellas solicitudes de cargas que el sistema no pudo verificar automáticamente.</li>
      <li><Text strong>Procesamiento de retiros:</Text> Completarás las solicitudes de retiro de fondos presentadas por los usuarios.</li>
    </ul>
    
    <Divider />
    
    <Row gutter={isMobile ? [12, 12] : [24, 24]}>
      <Col xs={24} md={12}>
        <Card 
          title={
            <Space>
              <DatabaseOutlined style={{ color: '#1890ff' }} />
              <Text strong>Cargas</Text>
            </Space>
          } 
          variant="outlined"
          size={isMobile ? "small" : "default"}
        >
          <Paragraph style={responsiveTextStyle(isMobile)}>
            Se reciben desde los chats con los jugadores, quienes envían el
            comprobante de transferencia. El sistema analiza
            automáticamente los comprobantes y los aprueba o los marca para revisión.
          </Paragraph>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card 
          title={
            <Space>
              <SwapOutlined style={{ color: '#52c41a' }} />
              <Text strong>Retiros</Text>
            </Space>
          } 
          variant="outlined"
          size={isMobile ? "small" : "default"}
        >
          <Paragraph style={responsiveTextStyle(isMobile)}>
            Al igual que las cargas, los retiros se solicitan desde el chat del
            jugador. El sistema verifica que el usuario tenga saldo disponible en la
            plataforma antes de procesar la solicitud y de ser aprobado, retira el saldo.
          </Paragraph>
        </Card>
      </Col>
    </Row>

    <Divider />

    <Divider orientation="left">Video explicativo</Divider>
    <Row>
      <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
        <LazyVideo 
          src="https://youtu.be/dvaRQ3ND_q8"
          title="Contexto y objetivo del sistema"
          width="100%"
        />
      </Col>
    </Row>

    <Divider orientation="left">Video de Login y Navegación</Divider>
    <Row>
      <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
        <LazyVideo 
          src="https://youtu.be/AsD2NuUb0YM"
          title="Login y Navegación"
          width="100%"
        />
      </Col>
    </Row>

  </ResponsiveCard>
)};

const RequestsPanel = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
  <ResponsiveCard $isMobile={isMobile}>
    <Title level={isMobile ? 5 : 4}>Panel de Solicitudes</Title>
    <Paragraph style={responsiveTextStyle(isMobile)}>
      En esta sección podremos:
    </Paragraph>
    
    <Row gutter={isMobile ? [12, 12] : [24, 24]} style={{ marginBottom: isMobile ? '10px' : '20px' }}>
      <Col xs={24} md={12}>
        <Card 
          title={
            <Space>
              <DatabaseOutlined style={{ color: '#1890ff' }} />
              <Text strong>Validar cargas manualmente</Text>
            </Space>
          } 
          variant="outlined"
          size={isMobile ? "small" : "default"}
        >
          <Paragraph style={responsiveTextStyle(isMobile)}>
            Revisar y aprobar las solicitudes de carga que el sistema no pudo validar automáticamente,
            verificando los comprobantes y la información proporcionada por los usuarios.
          </Paragraph>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card 
          title={
            <Space>
              <SwapOutlined style={{ color: '#52c41a' }} />
              <Text strong>Procesar retiros</Text>
            </Space>
          } 
          variant="outlined"
          size={isMobile ? "small" : "default"}
        >
          <Paragraph style={responsiveTextStyle(isMobile)}>
            Completar las solicitudes de retiro que los usuarios han hecho en la plataforma,
            verificando los datos bancarios y ejecutando las transferencias.
          </Paragraph>
        </Card>
      </Col>
    </Row>
    
    <Divider orientation="left">Características clave</Divider>
    
    <Row gutter={isMobile ? [8, 8] : [16, 16]}>
      <Col span={24}>
        <Card title="Acceso rápido" size={isMobile ? "small" : "default"}>
          <Paragraph style={responsiveTextStyle(isMobile)}>
            El panel es accesible desde cualquier página de la plataforma, permitiéndote responder rápidamente a las solicitudes 
            sin interrumpir tu flujo de trabajo actual.
          </Paragraph>
          <div style={{ textAlign: 'center', margin: isMobile ? '10px 0' : '20px 0' }}>
            <Image
              src="/gifs/AbrirCerrarRequestPanel.gif"
              alt="Abrir/Cerrar Panel de Requests"
              style={{ maxWidth: '100%' }}
            />
          </div>
        </Card>
      </Col>
    </Row>

    <Divider orientation="left">Secciones del Panel</Divider>
    
    <Paragraph style={responsiveTextStyle(isMobile)}>
      Para gestionar el panel tenemos 3 secciones:
    </Paragraph>
    
    <Row gutter={isMobile ? [8, 8] : [16, 16]}>
      <Col xs={24} md={8}>
        <Card 
          title={
            <Space>
              <InboxOutlined style={{ color: '#1890ff' }} />
              <Text strong>Validaciones pendientes</Text>
            </Space>
          } 
          variant="outlined"
          size={isMobile ? "small" : "default"}
        >
          <Paragraph style={responsiveTextStyle(isMobile)}>
            Aquí encontrarás todas las solicitudes que necesitan ser revisadas y validadas manualmente
            antes de ser procesadas.
          </Paragraph>
        </Card>
      </Col>
      
      <Col xs={24} md={8}>
        <Card 
          title={
            <Space>
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              <Text strong>Retiros aprobados</Text>
            </Space>
          } 
          variant="outlined"
          size={isMobile ? "small" : "default"}
        >
          <Paragraph style={responsiveTextStyle(isMobile)}>
            En esta sección verás los retiros ya aprobados que están pendientes de ser completados
            mediante transferencia a las cuentas de los usuarios.
          </Paragraph>
        </Card>
      </Col>
      
      <Col xs={24} md={8}>
        <Card 
          title={
            <Space>
              <CloseCircleOutlined style={{ color: '#722ed1' }} />
              <Text strong>Solicitudes procesadas</Text>
            </Space>
          } 
          variant="outlined"
          size={isMobile ? "small" : "default"}
        >
          <Paragraph style={responsiveTextStyle(isMobile)}>
            Aquí se archivan todas las solicitudes que ya han sido procesadas, ya sea completadas
            o rechazadas, para su consulta histórica.
          </Paragraph>
        </Card>
      </Col>
    </Row>

    <Divider orientation="left">Video explicativo</Divider>
    <Row>
      <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
        <LazyVideo 
          src="https://youtu.be/itwN6h-L5bo"
          title="Panel de Solicitudes"
          width="100%"
        />
      </Col>
    </Row>
  </ResponsiveCard>
)};

const PendingRequests = () => {
  const { selectedSubsection } = React.useContext(DocumentationContext);
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
    <ResponsiveCard $isMobile={isMobile}>
      <HighlightedSection
        $isSelected={selectedSubsection === 'validaciones-pendientes'} 
        $theme={theme as 'dark' | 'light'}
      >
        <Title level={isMobile ? 5 : 4}>Solicitudes Pendientes</Title>
        <Paragraph style={responsiveTextStyle(isMobile)}>
          En esta sección se muestran todas las solicitudes pendientes de validación manual.
        </Paragraph>
      </HighlightedSection>
      
      <Divider orientation="left">Estados de solicitudes</Divider>
      
      <Row gutter={isMobile ? [8, 8] : [16, 16]}>
        <Col span={24}>
          <HighlightedSection 
            $isSelected={selectedSubsection === 'validaciones-pendientes'} 
            $theme={theme as 'dark' | 'light'}
          >
            <Card 
              title={
                <Space>
                  <SyncOutlined style={{ color: '#1890ff' }} />
                  <Text strong>Estado "Pendientes"</Text>
                </Space>
              }
              size={isMobile ? "small" : "default"}
            >
              <Paragraph style={responsiveTextStyle(isMobile)}>
                Momentáneamente aparecen solicitudes "Pendientes" - estas son las que el sistema está validando automáticamente.
                Durante este estado, el sistema está procesando y analizando la información proporcionada.
              </Paragraph>
            </Card>
          </HighlightedSection>
        </Col>
        
        <Col span={24}>
          <Card 
            title={
              <Space>
                <InboxOutlined style={{ color: '#faad14' }} />
                <Text strong>Estado "Validar"</Text>
              </Space>
            }
            size={isMobile ? "small" : "default"}
          >
            <Paragraph style={responsiveTextStyle(isMobile)}>
              Cuando las solicitudes tengan este estado, deberás elegir si validarlas <Text strong style={{ color: '#52c41a' }}>✓ (validar)</Text> o 
              rechazarlas <Text strong style={{ color: '#ff4d4f' }}>✗ (rechazar)</Text> según corresponda tras revisar la información.
            </Paragraph>
          </Card>
        </Col>
      </Row>
      
      <Divider orientation="left">Gestión de solicitudes</Divider>
      
      <Row gutter={isMobile ? [8, 8] : [16, 16]}>
        <Col span={24}>
          <Card title="Detalles de solicitud" size={isMobile ? "small" : "default"}>
            <Paragraph style={responsiveTextStyle(isMobile)}>
              Puedes ver todos los detalles de una solicitud pendiente haciendo clic en ella. Esto mostrará información completa 
              como datos del usuario, monto, fecha, método de pago y el comprobante adjunto.
            </Paragraph>
            <div style={{ textAlign: 'center', margin: isMobile ? '10px 0' : '20px 0' }}>
              <OptimizedImage
                src="/gifs/DetailsPendingValidation.gif"
                alt="Ver detalles de solicitud pendiente"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </Card>
        </Col>
        
        <Col span={24}>
          <Card title="Validación de solicitudes" size={isMobile ? "small" : "default"}>
            <Paragraph style={responsiveTextStyle(isMobile)}>
              El proceso de validación te permite verificar la legitimidad de la solicitud y aprobarla para su procesamiento.
            </Paragraph>
            <div style={{ textAlign: 'center', margin: isMobile ? '10px 0' : '20px 0' }}>
              <OptimizedImage
                src="/gifs/ValidatePendingRequest.gif"
                alt="Validar solicitud pendiente"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </Card>
        </Col>
        
        <Col span={24}>
          <Card title="Rechazo de solicitudes" size={isMobile ? "small" : "default"}>
            <Paragraph style={responsiveTextStyle(isMobile)}>
              En caso de encontrar irregularidades o problemas, puedes rechazar la solicitud proporcionando un motivo claro.
            </Paragraph>
            <div style={{ textAlign: 'center', margin: isMobile ? '10px 0' : '20px 0' }}>
              <OptimizedImage
                src="/gifs/RejectingPendingValidation.gif"
                alt="Rechazar solicitud pendiente"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Divider orientation="left">Video explicativo</Divider>
      <Row>
        <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
          <LazyVideo 
            src="https://youtu.be/pbvsmU4UgNQ"
            title="Validaciones pendientes"
            width="100%"
          />
        </Col>
      </Row>
    </ResponsiveCard>
  );
};

const ApprovedWithdrawals = () => {
  const { selectedSubsection } = React.useContext(DocumentationContext);
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
    <ResponsiveCard $isMobile={isMobile}>
      <HighlightedSection
        $isSelected={selectedSubsection === 'retiros-aprobados'} 
        $theme={theme as 'dark' | 'light'}
      >
        <Title level={isMobile ? 5 : 4}>Retiros Aprobados</Title>
        <Paragraph style={responsiveTextStyle(isMobile)}>
          Esta sección muestra todos los retiros que han sido aprobados pero aún no han sido completados.
          Estos retiros están en espera de la transferencia efectiva a la cuenta del usuario.
        </Paragraph>
      </HighlightedSection>
      
      <Divider orientation="left">Ciclo de vida del retiro</Divider>
      
      <Row gutter={isMobile ? [8, 8] : [16, 16]} style={{ marginBottom: isMobile ? '15px' : '30px' }}>
        <Col xs={24} md={8}>
          <HighlightedSection 
            $isSelected={selectedSubsection === 'retiros-aprobados'} 
            $theme={theme as 'dark' | 'light'}
          >
            <Card 
              title={
                <Space>
                  <Text strong>1. Solicitud</Text>
                </Space>
              }
              variant="outlined"
              style={{ height: '100%', borderTop: '3px solid #1890ff' }}
styles={{
 header: { 
   backgroundColor: '#e6f7ff' 
 }
}}
              size={isMobile ? "small" : "default"}
            >
              <Paragraph style={responsiveTextStyle(isMobile)}>
                El usuario solicita el retiro a través del chat, indicando el monto que desea retirar.
              </Paragraph>
            </Card>
          </HighlightedSection>
        </Col>
        
        <Col xs={24} md={8}>
          <Card 
            title={
              <Space>
                <Text strong>2. Aprobación automática</Text>
              </Space>
            }
            variant="outlined"
            style={{ height: '100%', borderTop: '3px solid #52c41a' }}
styles={{
 header: { 
   backgroundColor: '#f6ffed' 
 }
}}
            size={isMobile ? "small" : "default"}
          >
            <Paragraph style={responsiveTextStyle(isMobile)}>
              El sistema automáticamente aprueba o rechaza el retiro.
            </Paragraph>
          </Card>
        </Col>
        
        <Col xs={24} md={8}>
          <Card 
            title={
              <Space>
                <Text strong>3. Completar retiro</Text>
              </Space>
            }
            variant="outlined"
            style={{ height: '100%', borderTop: '3px solid #722ed1' }}
styles={{
 header: { 
   backgroundColor: '#f9f0ff' 
 }
}}
            size={isMobile ? "small" : "default"}
          >
            <Paragraph style={responsiveTextStyle(isMobile)}>
              Nosotros como operadores debemos hacer la transferencia al cliente y completar la solicitud aprobada de retiro en el sistema.
            </Paragraph>
          </Card>
        </Col>
      </Row>
      
      <Card 
        style={{ 
          marginBottom: isMobile ? '10px' : '20px', 
          backgroundColor: '#f6ffed', 
          borderLeft: '4px solid #52c41a',
          borderRadius: '2px'
        }}
        size={isMobile ? "small" : "default"}
      >
        <Paragraph style={{ ...responsiveTextStyle(isMobile), margin: 0 }}>
          <Text strong>Nota importante:</Text> Si el retiro es aprobado, el saldo se descuenta automáticamente de la cuenta del usuario
        </Paragraph>
      </Card>
      
      <Divider orientation="left">Gestión de retiros</Divider>
      
      <Row gutter={isMobile ? [8, 8] : [16, 16]}>
        <Col span={24}>
          <Card title="Detalles del retiro" size={isMobile ? "small" : "default"}>
            <Paragraph style={responsiveTextStyle(isMobile)}>
              Accede a toda la información de un retiro aprobado, incluyendo datos bancarios, monto y fecha de aprobación.
            </Paragraph>
            <div style={{ textAlign: 'center', margin: isMobile ? '10px 0' : '20px 0' }}>
              <OptimizedImage
                src="/gifs/DetailsWithdraw.gif"
                alt="Ver detalles de retiro"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </Card>
        </Col>
        
        <Col span={24}>
          <Card title="Completar retiro" size={isMobile ? "small" : "default"}>
            <Paragraph style={responsiveTextStyle(isMobile)}>
              Marca un retiro como completado una vez que hayas realizado la transferencia al usuario.
              Esto actualizará el estado de la operación en el sistema.
            </Paragraph>
            <div style={{ textAlign: 'center', margin: isMobile ? '10px 0' : '20px 0' }}>
              <OptimizedImage
                src="/gifs/CompleteWithdraw.gif"
                alt="Completar retiro"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </Card>
        </Col>
        
        <Col span={24}>
          <Card title="Cancelar retiro" size={isMobile ? "small" : "default"}>
            <Paragraph style={responsiveTextStyle(isMobile)}>
              Si es necesario, puedes cancelar un retiro aprobado, indicando el motivo de la cancelación.
            </Paragraph>
            <div style={{ textAlign: 'center', margin: isMobile ? '10px 0' : '20px 0' }}>
              <OptimizedImage
                src="/gifs/CancelingWithDraw.gif"
                alt="Cancelar retiro"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Divider orientation="left">Video explicativo</Divider>
      <Row>
        <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
          <LazyVideo 
            src="https://youtu.be/Ea9EhSKD8P4"
            title="Retiros aprobados"
            width="100%"
          />
        </Col>
      </Row>
    </ResponsiveCard>
  );
};

const CompletedRejected = () => {
  const { selectedSubsection } = React.useContext(DocumentationContext);
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
    <ResponsiveCard $isMobile={isMobile}>
      <HighlightedSection
        $isSelected={selectedSubsection === 'completadas-rechazadas'} 
        $theme={theme as 'dark' | 'light'}
      >
        <Title level={isMobile ? 5 : 4}>Solicitudes Procesadas</Title>
        <Paragraph style={responsiveTextStyle(isMobile)}>
          En esta sección podrás revisar todas las operaciones que han sido completadas o rechazadas en el sistema.
          Es útil para tener un registro histórico y realizar seguimientos o análisis de transacciones pasadas.
        </Paragraph>
      </HighlightedSection>
      
      <Divider orientation="left">Gestión de historial</Divider>
      
      <Row gutter={isMobile ? [8, 8] : [16, 16]}>
        <Col span={24}>
          <HighlightedSection 
            $isSelected={selectedSubsection === 'completadas-rechazadas'} 
            $theme={theme as 'dark' | 'light'}
          >
            <Card title="Filtrado avanzado" size={isMobile ? "small" : "default"}>
              <Paragraph style={responsiveTextStyle(isMobile)}>
                Utiliza las opciones de filtrado para encontrar operaciones específicas por fecha, tipo, estado, monto o usuario.
              </Paragraph>
              <div style={{ textAlign: 'center', margin: isMobile ? '10px 0' : '20px 0' }}>
                <OptimizedImage
                  src="/gifs/Filtrado.gif"
                  alt="Filtrado avanzado"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            </Card>
          </HighlightedSection>
        </Col>
        
        <Col span={24}>
          <Card title="Detalles de operación" size={isMobile ? "small" : "default"}>
            <Paragraph style={responsiveTextStyle(isMobile)}>
              Accede a la información completa de cualquier operación histórica, incluyendo sus datos, comprobantes y estados.
            </Paragraph>
            <div style={{ textAlign: 'center', margin: isMobile ? '10px 0' : '20px 0' }}>
              <OptimizedImage
                src="/gifs/DetailsComplete.gif"
                alt="Ver detalles de operación completa"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </Card>
        </Col>
        
        <Col span={24}>
          <Card title="Reprocesamiento" size={isMobile ? "small" : "default"}>
            <Paragraph style={responsiveTextStyle(isMobile)}>
              En casos necesarios, puedes reprocesar operaciones completadas o rechazadas, reabriendo el caso para una nueva evaluación.
            </Paragraph>
            <div style={{ textAlign: 'center', margin: isMobile ? '10px 0' : '20px 0' }}>
              <OptimizedImage
                src="/gifs/ReprocesC&R.gif"
                alt="Reprocesar operación"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </Card>
        </Col>
        
        <Col span={24}>
          <Card title="Cancelar operación" size={isMobile ? "small" : "default"}>
            <Paragraph style={responsiveTextStyle(isMobile)}>
              Algunas operaciones pueden requerir cancelación o reversión después de haber sido completadas.
            </Paragraph>
            <div style={{ textAlign: 'center', margin: isMobile ? '10px 0' : '20px 0' }}>
              <OptimizedImage
                src="/gifs/CancelC&R.gif"
                alt="Cancelar operación"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Divider orientation="left">Video explicativo</Divider>
      <Row>
        <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
          <LazyVideo 
            src="https://youtu.be/thydT5GkaWM"
            title="Solicitudes procesadas"
            width="100%"
          />
        </Col>
      </Row>
    </ResponsiveCard>
  );
};

const OperationsManagement = () => {
  const { selectedSubsection } = React.useContext(DocumentationContext);
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
    <ResponsiveCard $isMobile={isMobile}>
      <Title level={isMobile ? 5 : 4}>Gestión de Operaciones</Title>
      <Paragraph style={responsiveTextStyle(isMobile)}>
        En esta sección se pueden ver todas las operaciones del sistema. Podrás visualizar y gestionar
        los diferentes tipos de transacciones realizadas.
      </Paragraph>
      
      <Divider orientation="left">Tipos de operaciones</Divider>
      
      <Row gutter={isMobile ? [8, 8] : [16, 16]}>
        <Col xs={24} md={8}>
          <Card variant="outlined" size={isMobile ? "small" : "default"}>
            <Space direction="vertical" align="center" style={{ width: '100%' }}>
              <DatabaseOutlined style={{ fontSize: isMobile ? 24 : 32, color: '#1890ff' }} />
              <Text strong>Depósitos</Text>
              <Paragraph style={{ textAlign: 'center', fontSize: isMobile ? '0.9em' : '1em' }}>
                Visualiza y gestiona todos los depósitos realizados por los usuarios en el sistema.
              </Paragraph>
            </Space>
          </Card>
        </Col>
        
        <Col xs={24} md={8}>
          <Card variant="outlined" size={isMobile ? "small" : "default"}>
            <Space direction="vertical" align="center" style={{ width: '100%' }}>
              <SwapOutlined style={{ fontSize: isMobile ? 24 : 32, color: '#52c41a' }} />
              <Text strong>Retiros</Text>
              <Paragraph style={{ textAlign: 'center', fontSize: isMobile ? '0.9em' : '1em' }}>
                Controla los retiros solicitados y procesados para los usuarios de la plataforma.
              </Paragraph>
            </Space>
          </Card>
        </Col>
        
        <Col xs={24} md={8}>
          <Card variant="outlined" size={isMobile ? "small" : "default"}>
            <Space direction="vertical" align="center" style={{ width: '100%' }}>
              <UserOutlined style={{ fontSize: isMobile ? 24 : 32, color: '#722ed1' }} />
              <Text strong>Creación de usuarios</Text>
              <Paragraph style={{ textAlign: 'center', fontSize: isMobile ? '0.9em' : '1em' }}>
                Monitorea los procesos de creación de nuevas cuentas de usuario en el sistema.
              </Paragraph>
            </Space>
          </Card>
        </Col>
      </Row>
      
      <Divider orientation="left">Acciones disponibles</Divider>
      
      <HighlightedSection 
        id="operaciones-modify" 
        $isSelected={selectedSubsection === 'operaciones-modify'} 
        $theme={theme as 'dark' | 'light'}
      >
        <Card 
          title={
            <Space>
              <EditOutlined style={{ color: '#faad14' }} />
              <Text strong>Modificación de montos</Text>
            </Space>
          }
          size={isMobile ? "small" : "default"}
        >
          <Paragraph style={responsiveTextStyle(isMobile)}>
            La única acción posible en esta sección es modificar el monto y bonus de depósitos/retiros en los cuales nos 
            hayamos equivocado y querramos corregir el valor en el sistema.
          </Paragraph>
          <Paragraph style={responsiveTextStyle(isMobile)}>
            <Text strong>Importante:</Text> Los cambios realizados aquí solo aplican al sistema interno y no generan 
            modificaciones en las plataformas externas.
          </Paragraph>
          <div style={{ 
            textAlign: 'center', 
            margin: isMobile ? '10px 0' : '20px 0', 
            padding: isMobile ? '5px' : '10px', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '8px' 
          }}>
            <OptimizedImage
              src="/gifs/modificacion_montos.gif"
              alt="Proceso de modificación de montos"
              style={{ maxWidth: '100%' }}
            />
          </div>
        </Card>
      </HighlightedSection>

      <Divider orientation="left">Video explicativo</Divider>
      <Row>
        <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
          <LazyVideo 
            src="https://youtu.be/jrxN5rou8RM"
            title="Operaciones"
            width="100%"
          />
        </Col>
      </Row>
    </ResponsiveCard>
  );
};

const UsersManagement = () => {
  const { selectedSubsection } = React.useContext(DocumentationContext);
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
    <ResponsiveCard $isMobile={isMobile}>
      <Title level={isMobile ? 5 : 4}>Gestión de Usuarios</Title>
      <Paragraph style={responsiveTextStyle(isMobile)}>
        En esta sección podemos visualizar los usuarios creados por los clientes desde WhatsApp. Si el usuario 
        aparece aquí, significa que en la plataforma se creó con el mismo nombre.
      </Paragraph>
      
      <Divider orientation="left">Funcionalidades clave</Divider>
      
      <Row gutter={isMobile ? [8, 8] : [16, 16]}>
        <Col span={24}>
          <Card 
            title={
              <Space>
                <UserOutlined style={{ color: '#1890ff' }} />
                <Text strong>Búsqueda y visualización de usuarios</Text>
              </Space>
            }
            size={isMobile ? "small" : "default"}
          >
            <Paragraph style={responsiveTextStyle(isMobile)}>
              Al utilizar el buscador y seleccionar a un cliente, podremos ver su celular y cuenta asignada,
              además de acceder a diversas acciones para gestionar al usuario.
            </Paragraph>
            <div style={{ 
              textAlign: 'center', 
              margin: isMobile ? '10px 0' : '20px 0', 
              padding: isMobile ? '5px' : '10px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '8px' 
            }}>
              <OptimizedImage
                src="/gifs/visualizacion_usuarios.gif"
                alt="Vista de búsqueda y selección de usuarios"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </Card>
        </Col>
      </Row>
      
      <Divider orientation="left">Acciones disponibles</Divider>
      
      <Row gutter={isMobile ? [8, 8] : [16, 16]}>
        <Col span={24} id="usuarios-balance">
          <HighlightedSection 
            $isSelected={selectedSubsection === 'usuarios-balance'} 
            $theme={theme as 'dark' | 'light'}
          >
            <Card 
              title={
                <Space>
                  <DatabaseOutlined style={{ color: '#1890ff' }} />
                  <Text strong>Chequear balance actual</Text>
                </Space>
              }
              variant="outlined"
              size={isMobile ? "small" : "default"}
            >
              <Paragraph style={responsiveTextStyle(isMobile)}>
                Visualiza el saldo disponible del cliente en la plataforma en tiempo real.
              </Paragraph>
              <div style={{ 
                textAlign: 'center', 
                margin: isMobile ? '10px 0' : '20px 0', 
                padding: isMobile ? '5px' : '10px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <OptimizedImage
                  src="/gifs/chequear_balance.gif"
                  alt="Vista de balance del usuario"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            </Card>
          </HighlightedSection>
        </Col>
        
        <Col span={24} id="usuarios-history">
          <HighlightedSection 
            $isSelected={selectedSubsection === 'usuarios-history'} 
            $theme={theme as 'dark' | 'light'}
          >
            <Card 
              title={
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <Text strong>Chequear historial</Text>
                </Space>
              }
              variant="outlined"
              size={isMobile ? "small" : "default"}
            >
              <Paragraph style={responsiveTextStyle(isMobile)}>
                Accede al historial completo de transacciones del cliente en la plataforma.
              </Paragraph>
              <div style={{ 
                textAlign: 'center', 
                margin: isMobile ? '10px 0' : '20px 0', 
                padding: isMobile ? '5px' : '10px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <OptimizedImage
                  src="/gifs/chequear_historial.gif"
                  alt="Vista de historial de transacciones"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            </Card>
          </HighlightedSection>
        </Col>
        
        <Col span={24} id="usuarios-credentials">
          <HighlightedSection 
            $isSelected={selectedSubsection === 'usuarios-credentials'} 
            $theme={theme as 'dark' | 'light'}
          >
            <Card 
              title={
                <Space>
                  <SwapOutlined style={{ color: '#faad14' }} />
                  <Text strong>Copiar credenciales</Text>
                </Space>
              }
              variant="outlined"
              size={isMobile ? "small" : "default"}
            >
              <Paragraph style={responsiveTextStyle(isMobile)}>
                Obtén y copia las credenciales del cliente para asistencia o resolución de problemas.
              </Paragraph>
              <div style={{ 
                textAlign: 'center', 
                margin: isMobile ? '10px 0' : '20px 0', 
                padding: isMobile ? '5px' : '10px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <OptimizedImage
                  src="/gifs/copiar_credenciales.gif"
                  alt="Copiado de credenciales de usuario"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            </Card>
          </HighlightedSection>
        </Col>
        
        <Col span={24} id="usuarios-deposit">
          <HighlightedSection 
            $isSelected={selectedSubsection === 'usuarios-deposit'} 
            $theme={theme as 'dark' | 'light'}
          >
            <Card 
              title={
                <Space>
                  <MoneyCollectOutlined style={{ color: '#1890ff' }} />
                  <Text strong>Hacer depósito de saldo</Text>
                </Space>
              }
              variant="outlined"
              size={isMobile ? "small" : "default"}
            >
              <Paragraph style={responsiveTextStyle(isMobile)}>
                Realiza un depósito de saldo (monto + bonus) con la posibilidad de incluir el comprobante 
                de pago y notificar al usuario.
              </Paragraph>
              <div style={{ 
                textAlign: 'center', 
                margin: isMobile ? '10px 0' : '20px 0', 
                padding: isMobile ? '5px' : '10px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <OptimizedImage
                  src="/gifs/deposito_saldo.gif"
                  alt="Proceso de depósito de saldo"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            </Card>
          </HighlightedSection>
        </Col>
        
        <Col span={24} id="usuarios-withdraw">
          <HighlightedSection 
            $isSelected={selectedSubsection === 'usuarios-withdraw'} 
            $theme={theme as 'dark' | 'light'}
          >
            <Card 
              title={
                <Space>
                  <SwapOutlined style={{ color: '#52c41a' }} />
                  <Text strong>Hacer retiro de saldo</Text>
                </Space>
              }
              variant="outlined"
              size={isMobile ? "small" : "default"}
            >
              <Paragraph style={responsiveTextStyle(isMobile)}>
                Procesa un retiro de saldo (monto + bonus) con la posibilidad de incluir el comprobante 
                de pago y notificar al usuario.
              </Paragraph>
              <div style={{ 
                textAlign: 'center', 
                margin: isMobile ? '10px 0' : '20px 0', 
                padding: isMobile ? '5px' : '10px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <OptimizedImage
                  src="/gifs/retiro_saldo.gif"
                  alt="Proceso de retiro de saldo"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            </Card>
          </HighlightedSection>
        </Col>
      </Row>

      <Divider orientation="left">Video explicativo</Divider>
      <Row>
        <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
          <LazyVideo 
            src="https://youtu.be/rNsUc2axjl8"
            title="Usuarios"
            width="100%"
          />
        </Col>
      </Row>
    </ResponsiveCard>
  );
};

const WalletPanel = () => {
  const { selectedSubsection } = React.useContext(DocumentationContext);
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
    <ResponsiveCard $isMobile={isMobile}>
      <HighlightedSection
        $isSelected={selectedSubsection === 'wallet'} 
        $theme={theme as 'dark' | 'light'}
      >
        <Title level={isMobile ? 5 : 4}>Panel de Billetera</Title>
        <Paragraph style={responsiveTextStyle(isMobile)}>
          Esta sección es la fuente de verdad del sistema. Todas las transferencias se podrán visualizar aquí;
          se presupone que si una transferencia no llegó a esta sección es porque no impactó en la billetera.
        </Paragraph>
      </HighlightedSection>
      
      <Divider orientation="left">Características principales</Divider>
      
      <Row gutter={isMobile ? [8, 8] : [16, 16]}>
        <Col span={24}>
          <HighlightedSection 
            id="wallet-general"
            $isSelected={selectedSubsection === 'wallet-general'}
            $theme={theme as 'dark' | 'light'}
          >
            <Card 
              title={
                <Space>
                  <WalletOutlined style={{ color: '#1890ff' }} />
                  <Text strong>Visualización de transferencias</Text>
                </Space>
              }
              variant="outlined"
              size={isMobile ? "small" : "default"}
            >
              <Paragraph style={responsiveTextStyle(isMobile)}>
                Podrás visualizar si una transferencia ya fue validada y, en caso afirmativo,
                expandir la información para ver con qué datos fue enlazada.
              </Paragraph>
              <div style={{ 
                textAlign: 'center', 
                margin: isMobile ? '10px 0' : '20px 0', 
                padding: isMobile ? '5px' : '10px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <OptimizedImage
                  src="/gifs/visualizacion_billetera.gif"
                  alt="Visualización de transferencias validadas"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            </Card>
          </HighlightedSection>
        </Col>
      </Row>
      
      <Divider orientation="left">Acciones disponibles</Divider>
      
      <Row gutter={isMobile ? [8, 8] : [16, 16]}>
        <Col span={24}>
          <HighlightedSection 
            id="wallet-modify-platform"
            $isSelected={selectedSubsection === 'wallet-modify-platform'}
            $theme={theme as 'dark' | 'light'}
          >
            <Card 
              title={
                <Space>
                  <EditOutlined style={{ color: '#faad14' }} />
                  <Text strong>Modificar plataforma</Text>
                </Space>
              }
              variant="outlined"
              size={isMobile ? "small" : "default"}
            >
              <Paragraph style={responsiveTextStyle(isMobile)}>
                Si notamos que una vinculación de transferencia dedujo mal el nombre de la plataforma resultante,
                podemos hacer clic y cambiar el nombre de la plataforma.
              </Paragraph>
              <div style={{ 
                textAlign: 'center', 
                margin: isMobile ? '10px 0' : '20px 0', 
                padding: isMobile ? '5px' : '10px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <OptimizedImage
                  src="/gifs/modificar_plataforma.gif"
                  alt="Proceso de modificación de plataforma"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            </Card>
          </HighlightedSection>
        </Col>
        
        <Col span={24}>
          <HighlightedSection 
            id="wallet-discontinue"
            $isSelected={selectedSubsection === 'wallet-discontinue'}
            $theme={theme as 'dark' | 'light'}
          >
            <Card 
              title={
                <Space>
                  <StopOutlined style={{ color: '#ff4d4f' }} />
                  <Text strong>Descontinuar transferencia</Text>
                </Space>
              }
              variant="outlined"
              size={isMobile ? "small" : "default"}
            >
              <Paragraph style={responsiveTextStyle(isMobile)}>
                Si sabemos que una transferencia jamás será vinculada, podemos descontinuarla
                para mantener el sistema organizado.
              </Paragraph>
              <div style={{ 
                textAlign: 'center', 
                margin: isMobile ? '10px 0' : '20px 0', 
                padding: isMobile ? '5px' : '10px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <OptimizedImage
                  src="/gifs/descontinuar_transferencia.gif"
                  alt="Proceso de descontinuación de transferencia"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            </Card>
          </HighlightedSection>
        </Col>
        
        <Col span={24}>
          <HighlightedSection 
            id="wallet-update"
            $isSelected={selectedSubsection === 'wallet-update'}
            $theme={theme as 'dark' | 'light'}
          >
            <Card 
              title={
                <Space>
                  <SyncOutlined style={{ color: '#52c41a' }} />
                  <Text strong>Actualizar transferencias</Text>
                </Space>
              }
              variant="outlined"
              size={isMobile ? "small" : "default"}
            >
              <Paragraph style={responsiveTextStyle(isMobile)}>
                Al hacer clic en el botón de recargar, se actualizarán las transferencias más recientes
                desde la billetera, permitiendo ver los cambios en tiempo real.
              </Paragraph>
              <div style={{ 
                textAlign: 'center', 
                margin: isMobile ? '10px 0' : '20px 0', 
                padding: isMobile ? '5px' : '10px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <OptimizedImage
                  src="/gifs/actualizar_transferencias.gif"
                  alt="Proceso de actualización de transferencias"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            </Card>
          </HighlightedSection>
        </Col>
      </Row>

      <Divider orientation="left">Video explicativo</Divider>
      <Row>
        <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
          <LazyVideo 
            src="https://youtu.be/kicCcs0YEvA"
            title="Wallet"
            width="100%"
          />
        </Col>
      </Row>
    </ResponsiveCard>
  );
};

// Componentes lazy cargados
const LazyIntroduction = lazy(() => 
  Promise.resolve({ default: Introduction })
);

const LazyRequestsPanel = lazy(() => 
  Promise.resolve({ default: RequestsPanel })
);

const LazyPendingRequests = lazy(() => 
  Promise.resolve({ default: PendingRequests })
);

const LazyApprovedWithdrawals = lazy(() => 
  Promise.resolve({ default: ApprovedWithdrawals })
);

const LazyCompletedRejected = lazy(() => 
  Promise.resolve({ default: CompletedRejected })
);

const LazyOperationsManagement = lazy(() => 
  Promise.resolve({ default: OperationsManagement })
);

const LazyUsersManagement = lazy(() => 
  Promise.resolve({ default: UsersManagement })
);

const LazyWalletPanel = lazy(() => 
  Promise.resolve({ default: WalletPanel })
);

const Documentation: React.FC<DocumentationProps> = ({ open, onClose }) => {
  const { theme } = useTheme();
  const contentRef = useRef<HTMLDivElement>(null);
  const [selectedKey, setSelectedKey] = useState<string>('overview');
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedSubsection, setSelectedSubsection] = useState<string>('');
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false);
  
  // Add preload function
  const preloadSectionVideos = useCallback((sectionId: string) => {
    const videosToPreload = sectionVideoMap[sectionId] || [];
    videosToPreload.forEach(videoSrc => {
      preloadVideoMetadata(videoSrc);
    });
  }, []);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Lista de secciones con sus IDs y componentes
  const sections = [
    { id: 'overview', label: 'Introducción', icon: <BookOutlined />, component: <LazyIntroduction /> },
    { id: 'panel-requests', label: 'Panel de Solicitudes', icon: <AppstoreOutlined />, component: <LazyRequestsPanel /> },
    { id: 'validaciones-pendientes', label: 'Solicitudes Pendientes', icon: <InboxOutlined />, component: <LazyPendingRequests /> },
    { id: 'retiros-aprobados', label: 'Retiros Aprobados', icon: <CheckCircleOutlined />, component: <LazyApprovedWithdrawals /> },
    { id: 'completadas-rechazadas', label: 'Completadas y Rechazadas', icon: <CloseCircleOutlined />, component: <LazyCompletedRejected /> },
    { id: 'wallet', label: 'Billetera', icon: <WalletOutlined />, component: <LazyWalletPanel /> },
    { id: 'usuarios', label: 'Usuarios', icon: <UserOutlined />, component: <LazyUsersManagement /> },
    { id: 'operaciones', label: 'Operaciones', icon: <SyncOutlined />, component: <LazyOperationsManagement /> }
  ];

  // Mapeo entre las claves del árbol y los IDs de los elementos en el DOM
  const sectionIdMap: Record<string, string> = {
    'overview': 'overview',
    'panel-requests': 'panel-requests',
    'requests': 'validaciones-pendientes',
    'validaciones-pendientes': 'validaciones-pendientes',
    'retiros-aprobados': 'retiros-aprobados',
    'completadas-rechazadas': 'completadas-rechazadas',
    'wallet': 'wallet',
    'wallet-general': 'wallet-general',
    'wallet-modify-platform': 'wallet-modify-platform',
    'wallet-discontinue': 'wallet-discontinue',
    'wallet-update': 'wallet-update',
    'usuarios': 'usuarios',
    'usuarios-balance': 'usuarios-balance',
    'usuarios-history': 'usuarios-history',
    'usuarios-credentials': 'usuarios-credentials',
    'usuarios-deposit': 'usuarios-deposit',
    'usuarios-withdraw': 'usuarios-withdraw',
    'operaciones': 'operaciones',
    'operaciones-modify': 'operaciones-modify',
  };
  const findTreeKeyForSection = (sectionId: string): string => {
  // Direct mapping check
  const directKey = Object.keys(sectionIdMap).find(key => sectionIdMap[key] === sectionId);
  
  // If this is "validaciones-pendientes", we need special handling
  if (sectionId === 'validaciones-pendientes') {
    return 'validaciones-pendientes';
  }
  
  return directKey || sectionId;
};

   // Add this to track sections during scrolling
  useEffect(() => {
    if (!open || !contentRef.current) return;
    
    // Create an observer to track which sections are visible
    const options = {
      root: contentRef.current,
      rootMargin: '-10% 0px -80% 0px', // Consider element visible when in top 20% of viewport
      threshold: 0
    };
    
const sectionObserver = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      const sectionId = entry.target.id;
      const treeKey = findTreeKeyForSection(sectionId);
      
      if (treeKey) {
        // For the parent-child relationship in the tree
        // Check if we need to expand a parent node
        const isChildNode = treeData.some(item => 
          item.children?.some(child => child.key === treeKey)
        );
        
        setSelectedKey(treeKey);
        setSelectedSubsection(treeKey);
        
        // If we need to ensure the parent node is expanded (if you're using defaultExpandAll this is less needed)
        if (isChildNode) {
          // Find parent node and ensure it's expanded
          const parentNode = treeData.find(item => 
            item.children?.some(child => child.key === treeKey)
          );
          if (parentNode) {
            // You might need to handle expanded keys here if not using defaultExpandAll
          }
        }
      }
    }
  });
}, options);

    
    // Observe all section elements
    const sectionElements = sections.map(section => 
      document.getElementById(section.id)
    ).filter(Boolean) as HTMLElement[];
    
    sectionElements.forEach(element => {
      sectionObserver.observe(element);
    });
    
    // Also observe subsection elements with IDs
    Object.values(sectionIdMap).forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        sectionObserver.observe(element);
      }
    });
    
    return () => {
      sectionElements.forEach(element => {
        sectionObserver.unobserve(element);
      });
      Object.values(sectionIdMap).forEach(id => {
        const element = document.getElementById(id);
        if (element) {
          sectionObserver.unobserve(element);
        }
      });
      sectionObserver.disconnect();
    };
  }, [open, sections, sectionIdMap, contentRef]);
 
  // Construir los datos para el Tree
const treeData: DataNode[] = [
  {
    title: 'Visión general',
    key: 'overview',
    icon: <BookOutlined />,
  },
  {
    title: 'Panel de Solicitudes',
    key: 'panel-requests',
    icon: <AppstoreOutlined />,
    children: [
      {
        title: 'Solicitudes Pendientes',
        key: 'validaciones-pendientes',
        icon: <InboxOutlined />,
      },
      {
        title: 'Retiros Aprobados',
        key: 'retiros-aprobados',
        icon: <CheckCircleOutlined />,
      },
      {
        title: 'Solicitudes Procesadas',
        key: 'completadas-rechazadas',
        icon: <CloseCircleOutlined />,
      },
    ],
  },
  {
    title: 'Billetera',
    key: 'wallet',
    icon: <WalletOutlined />,
    children: [
      {
        title: 'General',
        key: 'wallet-general',
        icon: <WalletOutlined />,
      },
      {
        title: 'Modificar plataforma',
        key: 'wallet-modify-platform',
        icon: <EditOutlined />,
      },
      {
        title: 'Descontinuar transferencia',
        key: 'wallet-discontinue',
        icon: <StopOutlined />,
      },
      {
        title: 'Actualizar transferencias',
        key: 'wallet-update',
        icon: <SyncOutlined />,
      }
    ],
  },
  {
    title: 'Usuarios',
    key: 'usuarios',
    icon: <UserOutlined />,
    children: [
      {
        title: 'Balance actual',
        key: 'usuarios-balance',
        icon: <DatabaseOutlined />,
      },
      {
        title: 'Historial',
        key: 'usuarios-history',
        icon: <CheckCircleOutlined />,
      },
      {
        title: 'Copiar credenciales',
        key: 'usuarios-credentials',
        icon: <SwapOutlined />,
      },
      {
        title: 'Depósito',
        key: 'usuarios-deposit',
        icon: <MoneyCollectOutlined />,
      },
      {
        title: 'Retiro',
        key: 'usuarios-withdraw',
        icon: <SwapOutlined />,
      }
    ],
  },
  {
    title: 'Operaciones',
    key: 'operaciones',
    icon: <SyncOutlined />,
    children: [
      {
        title: 'Modificación de montos',
        key: 'operaciones-modify',
        icon: <EditOutlined />,
      }
    ],
  },
];


  // Mobile responsive scroll function with optimized preloading
  const scrollToSection = useCallback((sectionId: string) => {
    setSelectedKey(sectionId);
    setSelectedSubsection(sectionId);
    
    // Preload videos solo si no está en modo móvil para reducir peticiones en dispositivos con menos recursos
    if (!isMobile) {
      // Preload videos with a small delay to not block UI
      setTimeout(() => {
        preloadSectionVideos(sectionId);
        const parentSection = findParentSection(sectionId, treeData);
        if (parentSection && parentSection !== sectionId) {
          preloadSectionVideos(parentSection);
        }
      }, 1000);
    }
    
    // On mobile, hide sidebar after selection
    if (isMobile) {
      setSidebarVisible(false);
    }
    
    // Determine the element ID
    const elementId = sectionIdMap[sectionId] || sectionId;
    
    // Schedule scroll after rendering
    setTimeout(() => {
      const element = document.getElementById(elementId);
      
      if (element && contentRef.current) {
        const containerRect = contentRef.current.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top;
        
        contentRef.current.scrollTo({
          top: contentRef.current.scrollTop + relativeTop - 20,
          behavior: 'smooth'
        });
      }
    }, 50);
  }, [preloadSectionVideos, isMobile, treeData, sectionIdMap]);

  // Resetear scroll cuando se abre el drawer
  useEffect(() => {
    if (open) {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
      setSelectedKey('overview');
      setSelectedSubsection('overview');
      setLoading(true);
      
      // Simular tiempo de carga para mostrar el skeleton
      const timer = setTimeout(() => {
        setLoading(false);
        
        // Precargar solo el video de la sección de inicio después de cargar el componente
        // y solo si no está en modo móvil
        if (!isMobile) {
          setTimeout(() => {
            preloadSectionVideos('overview');
          }, 2000); // Retardo adicional para asegurar que la UI ya está estable
        }
      }, 600);
      
      return () => clearTimeout(timer);
    }
  }, [open, isMobile, preloadSectionVideos]);

  // Función para renderizar secciones con IDs específicos para subsecciones
  const renderSectionWithSubsections = (section: any) => {
    return (
      <div 
        key={section.id} 
        id={section.id} 
        style={{ 
          scrollMarginTop: '20px', 
          marginBottom: isMobile ? '30px' : '60px',
          paddingTop: isMobile ? '10px' : '20px'
        }}
      >
        <Suspense fallback={<SectionSkeleton />}>
          {loading ? <SectionSkeleton /> : section.component}
        </Suspense>
      </div>
    );
  };

  return (
    <DocumentationContext.Provider value={{ 
      selectedSubsection, 
      setSelectedSubsection, 
      preloadSectionVideos 
    }}>
      <Drawer
        title={<Space><BookOutlined /> Documentación</Space>}
        placement="right"
        onClose={onClose}
        open={open}
        width={isMobile ? '100%' : 1200}
styles={{ 
  body: { 
    padding: isMobile ? '8px 0' : undefined 
  }
}}
        footer={
          <Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
            © {new Date().getFullYear()} - The Real Project
          </Text>
        }
      >
        <DocContainer>
          {/* Sidebar container - shown/hidden on mobile */}
        <MobileBackdrop 
  $visible={sidebarVisible} 
  onClick={() => setSidebarVisible(false)}
/>
          <DocSidebarContainer 
            $theme={theme as 'dark' | 'light'} 
            $isMobile={isMobile}
            $visible={sidebarVisible}
          >
            {isMobile && (
              <div style={{ 
                padding: '8px 0', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
              }}>
                <Text strong>Navegación</Text>
                <Button 
                  size="small" 
                  onClick={() => setSidebarVisible(false)}
                  icon={<LeftOutlined />}
                >
                  Cerrar
                </Button>
              </div>
            )}
            
            <StyledTree
              showIcon
              defaultExpandAll={true}
              defaultExpandParent={true}
              selectedKeys={[selectedKey]}
              onSelect={(selectedKeys) => {
                if (selectedKeys.length > 0) {
                  scrollToSection(selectedKeys[0] as string);
                }
              }}
              treeData={treeData}
              $theme={theme as 'dark' | 'light'}
            />
          </DocSidebarContainer>
          
          {/* Main content area */}
          <ContentArea 
            ref={contentRef}
            $isMobile={isMobile}
          >
            {sections.map(renderSectionWithSubsections)}
          </ContentArea>
          
          {/* Mobile navigation toggle */}
<MobileNavToggle 
  type="primary" 
  shape="circle" 
  size="large"
  icon={<MenuOutlined style={{ fontSize: '20px' }} />} 
  onClick={() => setSidebarVisible(true)}
/>
        </DocContainer>
      </Drawer>
    </DocumentationContext.Provider>
  );
};

export default Documentation;